import React, { useState, useCallback, useRef, type MutableRefObject } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { queryClient } from "@/lib/queryClient";
import { aiService, type OpenRouterModel as AIServiceModel } from "@/services/aiService";
import { creditsService } from "@/services/creditsService";
import { ensureKnowledgeBaseItem } from "@/queries/knowledgeBaseDocuments";
import { superPromptsService } from "@/services/superPromptsService";
import { contentPrefetchService } from "@/services/contentPrefetchService";
import { type ContextPayload, type SourceContext } from "@/services/contextOrchestrator";
import {
  buildContextAwareSystemPromptCore,
  buildGitHubChatSystemExtension,
  buildThinkingChatSystemExtension,
  getExternalSuperPromptSystemBlock,
  buildWorkflowForkChatSystemExtension,
} from "@/services/chatSystemPromptForThread";
import { setRepoEditTurnContext } from "@/services/repoEditContextStore";
import { buildDsCoderSystemExtension } from "@/services/dsCoder/coderContextBuilder";
import { takePendingRagScope } from "@/services/pendingRagScopeStore";
import { ragScopeService } from "@/services/ragScopeService";
import { mapToRecord } from "@/lib/citationUtils";
import { createChatStreamTimingSession, getChatStreamTimeoutMs, getChatStreamUiThrottleMs } from "@/lib/chatStreamTiming";
import { limitChatHistoryForApi, logOutboundChatPayload, messageContentToText } from "@/lib/chatHistoryLimits";
import { yieldToMain } from "@/lib/yieldToMain";
import type { Source, WebSearchCitation } from "@shared/schema";
import { resolveOpenRouterModelId } from "@/lib/openRouterModelAliases";
import { parseChatError, type ChatErrorKind, type ParsedChatError } from "@/lib/chatErrorCatalog";
import { usePendingThreadPersistence } from "@/hooks/usePendingThreadPersistence";
import type { PendingThreadMetadata } from "@/lib/pendingThreadStore";
import { prependThreadToSummariesCache } from "@/lib/sidebarRecencyCache";
import { chatThreadsService } from "@/services/chatThreadsService";
import { operatingLoopService } from "@/services/operatingLoopService";
import type { OperatingTurn } from "@shared/operatingLoopRun";
import { tokenSavingsService } from "@/services/tokenSavingsService";
import { stopPathToLever } from "@shared/tokenSavings";

async function recordChatStopSavings(
  userId: string,
  path: string,
  threadId: string | null | undefined,
): Promise<void> {
  const lever = stopPathToLever(path);
  const baseline =
    lever === "drawer" || lever === "crisis_stop" || lever === "private_hold"
      ? await tokenSavingsService.averageAiRoomTokens(userId)
      : null;
  await tokenSavingsService.recordEvent({
    userId,
    surface: "chat",
    lever,
    baselineTokens: baseline,
    actualTokens: 0,
    callsAvoided: 1,
    threadId: threadId ?? null,
  });
}

export type ChatMessageStatus = "complete" | "partial" | "error";

export interface ChatSendMessage {
  id: string;
  role: "user" | "assistant";
  content: string | unknown;
  timestamp: Date;
  citations?: WebSearchCitation[];
  contextCitations?: Record<number, SourceContext>;
  images?: Array<{ url: string; detail?: string }>;
  model?: string;
  autoSelected?: boolean;
  status?: ChatMessageStatus;
  errorMessage?: string;
  errorKind?: ChatErrorKind;
  receiptLine?: string;
}

export type ChatSendOptions = {
  /** Re-run the assistant for an existing user turn without duplicating the user bubble. */
  retryUserMessageId?: string;
};

function serializeChatMessages(msgs: ChatSendMessage[]) {
  return msgs.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : (m.timestamp as string),
    citations: m.citations ? [...m.citations] : undefined,
    contextCitations: m.contextCitations ? { ...m.contextCitations } : undefined,
    images: m.images ? [...m.images] : undefined,
    model: m.model,
    autoSelected: m.autoSelected,
    status: m.status,
    errorMessage: m.errorMessage,
    errorKind: m.errorKind,
    receiptLine: m.receiptLine,
  }));
}

export function getContentType(fileType: string): string {
  if (fileType.startsWith("image/")) return "image";
  if (fileType.startsWith("audio/")) return "audio";
  if (fileType.startsWith("video/")) return "video";
  if (fileType === "application/pdf") return "pdf";
  return "image";
}

async function fetchSourceAttachments(
  sources: Source[]
): Promise<Array<{ type: string; data: string; filename: string; sourceTitle?: string; sourceDescription?: string }>> {
  const tasks = sources.flatMap((source) => {
    const attachments = Array.isArray(source.attachments) ? source.attachments : [];
    return attachments.map(async (attachment) => {
      if (!attachment.url) return null;
      try {
        const response = await fetch(attachment.url);
        if (!response.ok) return null;
        const blob = await response.blob();
        const base64data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const contentType =
          blob.type ||
          (attachment.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)
            ? "image/jpeg"
            : attachment.url.match(/\.(mp3|wav|ogg)$/i)
              ? "audio/mpeg"
              : attachment.url.match(/\.(mp4|webm|mov)$/i)
                ? "video/mp4"
                : attachment.url.match(/\.pdf$/i)
                  ? "application/pdf"
                  : "image/jpeg");
        return {
          type: getContentType(contentType),
          data: base64data,
          filename: attachment.filename || "attachment",
          sourceTitle: source.title,
          sourceDescription: source.content?.substring?.(0, 200),
        };
      } catch (e) {
        console.error(`Failed to fetch attachment ${attachment.url}:`, e);
        return null;
      }
    });
  });

  const results = await Promise.all(tasks);
  return results.filter(
    (item): item is { type: string; data: string; filename: string; sourceTitle?: string; sourceDescription?: string } =>
      Boolean(item)
  );
}

const MAX_KB_ATTACH_CHARS = 100_000;
const KB_ATTACH_EMPTY_BODY =
  "(This document has no saved text in the library yet. Add content in the Documents panel or Library and save, then send again.)";

/** Knowledge docs from Attach & Reference → Source Docs: inline into the user message so the model always receives them (RAG off, empty system context, or fetch gaps). */
async function buildKnowledgeAttachmentSuffix(documentIds: string[]): Promise<string> {
  if (!documentIds.length) return "";
  const parts: string[] = [];
  for (const id of documentIds) {
    try {
      const doc = await ensureKnowledgeBaseItem(queryClient, id);
      if (!doc) continue;
      const raw = String(doc.content ?? "").trim();
      let body = raw || KB_ATTACH_EMPTY_BODY;
      if (body.length > MAX_KB_ATTACH_CHARS) {
        body = `${body.slice(0, MAX_KB_ATTACH_CHARS)}\n...[truncated]`;
      }
      parts.push(`### Attached: ${doc.title?.trim() || "Untitled"}\n\n${body}`);
    } catch (e) {
      console.error("[useChatSend] Failed to load attached knowledge doc:", id, e);
    }
  }
  if (!parts.length) return "";
  return `\n\n---\n[Documents attached via Attach & Reference]\n\n${parts.join("\n\n---\n\n")}`;
}

export interface UseChatSendOptions {
  messages: ChatSendMessage[];
  updateMessages: (u: any) => void;
  input: string;
  setInput: (v: string) => void;
  currentThreadId: string | null;
  setCurrentThreadId: (id: string | null) => void;
  workspaceId?: string | null;
  spaceContextId?: string | null;
  createThreadMutation: { mutateAsync: (x: any) => Promise<any> };
  updateThreadMutation: { mutateAsync: (x: any) => Promise<any> };
  generateMetadataMutation: { mutate: (id: string) => void };
  shouldAutoTitle?: (threadId: string) => boolean;
  isResearching: boolean;
  isRecording: boolean;
  onStopRecordingAndRequestSubmit?: () => void;
  deepResearchMode: boolean;
  onDeepResearch: () => Promise<void>;
  selectedSpaceIds: string[];
  selectedTopicId: string | null;
  topicIdFromUrlRef?: MutableRefObject<string | null>; // Ref to preserve topicId from URL navigation
  spaceTopics: Array<{ id: string; spaceId: string }>;
  allTopics: Array<{ id: string; spaceId: string }>;
  isTopicsFetching: boolean;
  allSelectedSources: Source[];
  fileAttached: Array<{ type: string; data: string; filename?: string; meta?: { source: string; url?: string; resolution?: string; userAgent?: string } }>;
  fileAttachedRef?: MutableRefObject<Array<{ type: string; data: string; filename?: string; meta?: unknown }>>;
  setFileAttached: (v: any) => void;
  selectedDocumentIds: string[];
  usePersonalization: boolean;
  hasPersonalizationData: boolean;
  useSourceOfTruth: boolean;
  userProfile: any;
  selectedAgentId: string | null;
  selectedSuperPrompt: { id: string; promptTemplate: string; isSystem?: boolean } | null;
  selectedWorkflow: { id: string } | null;
  selectedModel: string;
  setSelectedModel: (v: string) => void;
  modelInfo: any;
  setModelInfo: (v: any) => void;
  availableModels: Array<{ id: string; supported_parameters?: string[] } & Record<string, unknown>>;
  isLoadingModels?: boolean;
  isModelsFetchError?: boolean;
  modelsFetchError?: unknown;
  refetchModels?: () => void;
  curatedModels: Array<{ id: string; category: string; supportsThinking?: boolean }>;
  availableCuratedModels: Array<{ id: string; category: string; supportsThinking?: boolean }>;
  autoSelectBest: boolean;
  thinkingMode: boolean;
  webSearchMode: string;
  selectedGitHubRepo: { fullName: string; owner: string; name: string } | null;
  selectedGitHubRepos?: Array<{ fullName: string; owner: string; name: string }>;
  cachedRepoContextRef: MutableRefObject<any>;
  cachedRepoContext: any;
  cachedRepoContexts?: Record<string, unknown>;
  isLoadingRepoContext: boolean;
  setServerPersonalization: (v: any) => void;
  user: { id: string } | null;
  completeMilestone: (id: string) => unknown;
  hasMilestone: (id: string) => boolean;
  contextMode: string;
  chatMode: string;
  useRAG?: boolean;
  useFullScope?: boolean;
}

function buildClassifyAndSelectModel(
  availableCuratedModels: Array<{ id: string; category: string }>,
  selectedModel: string
): (query: string) => string {
  return (query: string) => {
    const codePatterns = /\b(code|function|debug|error|programming|javascript|python|typescript|react|api|bug|fix|implement|build|develop|syntax|variable|class|method|algorithm)\b/i;
    if (codePatterns.test(query)) {
      const m = availableCuratedModels.find((x) => x.category === "code");
      if (m) return m.id;
    }
    const mathPatterns = /\b(math|calculate|solve|equation|proof|logic|reasoning|analyze|why|how does|explain step|think through|complex|difficult)\b/i;
    if (mathPatterns.test(query)) {
      const m = availableCuratedModels.find((x) => x.category === "thinking");
      if (m) return m.id;
    }
    const creativePatterns = /\b(write|story|creative|poem|essay|article|blog|content|narrative|fiction|character|draft|edit|rewrite|summarize)\b/i;
    if (creativePatterns.test(query)) {
      const m = availableCuratedModels.find((x) => x.category === "creative");
      if (m) return m.id;
    }
    const quickPatterns = /^(what is|who is|when|where|define|list|name)\b/i;
    if (quickPatterns.test(query) && query.length < 100) {
      const m = availableCuratedModels.find((x) => x.category === "fast");
      if (m) return m.id;
    }
    const general = availableCuratedModels.find((x) => x.category === "general");
    if (general) return general.id;
    return selectedModel || "openai/gpt-4o-mini";
  };
}

export function useChatSend(opts: UseChatSendOptions) {
  const {
    messages,
    updateMessages,
    input,
    setInput,
    currentThreadId,
    setCurrentThreadId,
    workspaceId,
    spaceContextId,
    createThreadMutation,
    updateThreadMutation,
    generateMetadataMutation,
    shouldAutoTitle,
    isResearching,
    isRecording,
    onStopRecordingAndRequestSubmit,
    deepResearchMode,
    onDeepResearch,
    selectedSpaceIds,
    selectedTopicId,
    topicIdFromUrlRef,
    spaceTopics,
    allTopics,
    isTopicsFetching,
    allSelectedSources,
    fileAttached,
    setFileAttached,
    selectedDocumentIds,
    usePersonalization,
    hasPersonalizationData,
    useSourceOfTruth,
    userProfile,
    selectedAgentId,
    selectedSuperPrompt,
    selectedWorkflow,
    selectedModel,
    setSelectedModel,
    modelInfo,
    setModelInfo,
    availableModels,
    isLoadingModels = false,
    isModelsFetchError = false,
    modelsFetchError,
    refetchModels,
    curatedModels,
    availableCuratedModels,
    autoSelectBest,
    thinkingMode,
    webSearchMode,
    selectedGitHubRepo,
    selectedGitHubRepos,
    cachedRepoContextRef,
    cachedRepoContext,
    cachedRepoContexts,
    isLoadingRepoContext,
    setServerPersonalization,
    user,
    completeMilestone,
    hasMilestone,
    contextMode,
    chatMode,
    useRAG = true,
    useFullScope = false,
  } = opts;

  const [, setLocation] = useLocation();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingThinkingContent, setStreamingThinkingContent] = useState("");
  const { toast } = useToast();
  const classifyAndSelectModel = buildClassifyAndSelectModel(availableCuratedModels, selectedModel);

  const pendingPersistence = usePendingThreadPersistence({
    userId: user?.id ?? null,
    workspaceId: workspaceId ?? null,
    spaceContextId: spaceContextId ?? null,
    generateMetadata: (id) => generateMetadataMutation.mutate(id),
    shouldAutoTitle,
    onVerified: (threadId) => {
      setCurrentThreadId(threadId);
      const params = new URLSearchParams(window.location.search);
      params.set("threadId", threadId);
      setLocation(`/chat?${params.toString()}`);
      void queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/chat-threads", threadId] });
      void chatThreadsService
        .getChatThread(threadId, workspaceId ?? null, spaceContextId ?? null)
        .then((thread) => {
          if (!thread) return;
          prependThreadToSummariesCache({
            ...(thread as Record<string, unknown>),
            messageCount: Array.isArray(thread.messages) ? thread.messages.length : 0,
            updatedAt: thread.updatedAt ?? new Date().toISOString(),
          });
          queryClient.setQueryData(
            ["/api/chat-threads", threadId, workspaceId ?? "personal", spaceContextId ?? "none"],
            thread
          );
        })
        .catch((err) => console.error("[useChatSend] Failed to refresh thread after verify:", err));
    },
  });

  const handleSend = useCallback(
    async (overrideInput?: string, sendOptions?: ChatSendOptions) => {
      const retryUserMessageId = sendOptions?.retryUserMessageId;
      let messageContent = overrideInput ?? input;

      if (retryUserMessageId) {
        const userIdx = messages.findIndex((m) => m.id === retryUserMessageId && m.role === "user");
        if (userIdx === -1 || isStreaming || isResearching) return;
      } else {
        const hasText = String(messageContent ?? "").trim().length > 0;
        const hasAttachments =
          fileAttached.length > 0 ||
          selectedDocumentIds.length > 0 ||
          allSelectedSources.some((s) => Array.isArray(s.attachments) && s.attachments.length > 0);
        if ((!hasText && !hasAttachments) || isStreaming || isResearching) return;
      }

      if (isRecording && onStopRecordingAndRequestSubmit) {
        onStopRecordingAndRequestSubmit();
        return;
      }

      if (deepResearchMode) {
        const drGate = await creditsService.assertPositiveBalanceForAi();
        if (!drGate.ok) {
          toast({
            title: "Credits depleted",
            description: "Purchase credits to continue using AI features.",
            variant: "destructive",
          });
          setLocation("/pricing");
          return;
        }
        await onDeepResearch();
        return;
      }

      const creditGate = await creditsService.assertPositiveBalanceForAi();
      if (!creditGate.ok) {
        toast({
          title: "Credits depleted",
          description: "Purchase credits to continue using AI features.",
          variant: "destructive",
        });
        setLocation("/pricing");
        return;
      }

      if (availableModels.length === 0) {
        if (isLoadingModels) {
          toast({
            title: "Loading models…",
            description: "Fetching available AI models. Try again in a moment.",
          });
          return;
        }
        if (isModelsFetchError) {
          const errMsg =
            modelsFetchError instanceof Error
              ? modelsFetchError.message
              : typeof modelsFetchError === "string"
                ? modelsFetchError
                : "Could not reach the AI model service.";
          toast({
            title: "Could not load models",
            description: errMsg,
            variant: "destructive",
            action: refetchModels
              ? React.createElement(
                  ToastAction,
                  { altText: "Retry", onClick: () => refetchModels() },
                  "Retry"
                )
              : undefined,
          });
          return;
        }
        toast({
          title: "Models unavailable",
          description: "Could not load AI models. Open Tools and tap “Check for new models”, or refresh the page.",
          variant: "destructive",
        });
        return;
      }

      setServerPersonalization(null);
      let activeThreadId = currentThreadId;
      pendingPersistence.resetDraftSession();
      const chatTiming = createChatStreamTimingSession(activeThreadId);
      // Use topicId from URL ref as fallback if selectedTopicId is null (preserves topicId from URL navigation)
      const refTopicId = topicIdFromUrlRef?.current ?? null;
      const topicIdAtStart = selectedTopicId || refTopicId;
      console.log('[useChatSend] TopicId capture:', {
        selectedTopicId,
        refTopicId,
        refExists: !!topicIdFromUrlRef,
        refCurrent: topicIdFromUrlRef?.current,
        finalTopicId: topicIdAtStart
      });
      let spaceIdsAtStart = [...selectedSpaceIds];

      if (topicIdAtStart) {
        const topicSpaces = spaceTopics.filter((t) => t.id === topicIdAtStart).map((t) => t.spaceId);
        for (const id of topicSpaces) {
          if (!spaceIdsAtStart.includes(id)) spaceIdsAtStart.push(id);
        }
        if (topicSpaces.length === 0 && !isTopicsFetching && allTopics.length > 0) {
          const t = allTopics.find((x) => x.id === topicIdAtStart);
          if (t && !spaceIdsAtStart.includes(t.spaceId)) spaceIdsAtStart.push(t.spaceId);
        }
        if (topicSpaces.length === 0 && spaceIdsAtStart.length === 0 && selectedSpaceIds.length > 0) {
          spaceIdsAtStart = [...selectedSpaceIds];
        }
      }

      const [kbSuffix, sourceAttachments] = await Promise.all([
        buildKnowledgeAttachmentSuffix(selectedDocumentIds),
        fetchSourceAttachments(allSelectedSources),
      ]);
      chatTiming?.mark("attachments_done");
      let userTextForMessage = String(messageContent ?? "");
      if (kbSuffix) {
        userTextForMessage = (
          userTextForMessage.trim() ? userTextForMessage : "Please read the attached document(s) below."
        ) + kbSuffix;
      }
      const allAttachments = [
        ...fileAttached.map((f) => ({ ...f, isUserUpload: true })),
        ...sourceAttachments.map((a) => ({ ...a, isUserUpload: false })),
      ];

      let draftStarted = false;
      const buildPersistMetadata = (): PendingThreadMetadata => ({
        contextMode,
        chatMode,
        agentId: selectedAgentId,
        linkedSpaceIds: spaceIdsAtStart,
        topicId: topicIdAtStart,
        attachedGitHubRepos: selectedGitHubRepos?.length ? selectedGitHubRepos : [],
      });

      const syncThreadToDatabase = async (
        msgs: ChatSendMessage[],
        threadId: string | null,
        phase: "early" | "final" = "final"
      ): Promise<string | null> => {
        const serialized = serializeChatMessages(msgs);
        if (serialized.length === 0) return threadId;
        if (!pendingPersistence.hasActiveDraft()) {
          pendingPersistence.beginDraft(serialized, buildPersistMetadata(), threadId);
          draftStarted = true;
        } else if (!draftStarted) {
          pendingPersistence.beginDraft(serialized, buildPersistMetadata(), threadId);
          draftStarted = true;
        } else {
          pendingPersistence.updateLocalMessages(serialized);
        }
        const nextId = await pendingPersistence.runSync({ phase });
        return nextId ?? threadId;
      };

      let pendingMessages: ChatSendMessage[];
      let currentMessages: ChatSendMessage[];
      let userMessage: ChatSendMessage;

      if (retryUserMessageId) {
        const userIdx = messages.findIndex((m) => m.id === retryUserMessageId && m.role === "user");
        if (userIdx === -1) return;
        userMessage = messages[userIdx] as ChatSendMessage;
        messageContent = messageContentToText(userMessage.content);
        currentMessages = messages.slice(0, userIdx + 1) as ChatSendMessage[];
        pendingMessages = [...currentMessages];
        updateMessages(currentMessages);
      } else {
        userMessage = {
          id: Date.now().toString(),
          role: "user",
          content: userTextForMessage,
          timestamp: new Date(),
        };

        if (allAttachments.length > 0) {
          let textForContent = String(userTextForMessage ?? "").trim() || "Please see the attached file(s).";
          const screenMeta = allAttachments.find((a: any) => a.meta?.source === "screen-capture")?.meta as { url?: string; resolution?: string; userAgent?: string } | undefined;
          if (screenMeta && (screenMeta.url || screenMeta.resolution || screenMeta.userAgent)) {
            const parts: string[] = [];
            if (screenMeta.url) parts.push(`URL: ${screenMeta.url}`);
            if (screenMeta.resolution) parts.push(`Resolution: ${screenMeta.resolution}`);
            if (screenMeta.userAgent) parts.push(`Browser: ${screenMeta.userAgent}`);
            if (parts.length) textForContent += `\n\n[Screen context: ${parts.join(" | ")}]`;
          }
          const contentArray: any[] = [{ type: "text", text: textForContent }];
          for (const att of allAttachments) {
            if (att.type === "image") contentArray.push({ type: "image_url", image_url: { url: att.data } });
            else if (att.type === "audio") contentArray.push({ type: "input_audio", input_audio: { data: att.data, format: "wav" } });
            else if (att.type === "video") contentArray.push({ type: "video_url", video_url: { url: att.data } });
            else if (att.type === "pdf") contentArray.push({ type: "file", file: { filename: att.filename, file_data: att.data } });
          }
          userMessage.content = contentArray;
        }

        pendingMessages = [...messages, userMessage];
        currentMessages = [...messages, userMessage];
        updateMessages(currentMessages);
        setInput("");
      }

      setIsStreaming(true);
      setStreamingThinkingContent("");
      await yieldToMain();

      try {
        const priorThreadId = activeThreadId;
        const earlyThreadId = await syncThreadToDatabase(pendingMessages, activeThreadId, "early");
        if (earlyThreadId) activeThreadId = earlyThreadId;
        // New Chat: apply pending multi-workspace RAG scope once the thread exists.
        if (activeThreadId && !priorThreadId) {
          const pendingScope = takePendingRagScope();
          if (pendingScope && pendingScope.length > 0) {
            try {
              await ragScopeService.setThreadScope(activeThreadId, pendingScope);
            } catch (scopeErr) {
              console.warn("[useChatSend] Failed to apply pending RAG scope:", scopeErr);
            }
          }
        }
      } catch (err) {
        console.error("[useChatSend] Early persist failed:", err);
        toast({
          title: "Not saved to database yet",
          description:
            "Your message is stored in browser local storage. We will keep retrying to save to the database.",
          variant: "destructive",
        });
      }

      const assistantId = (Date.now() + 1).toString();
      let assistantMessageCreated = false;
      let fullContent = "";
      let streamTotalTokens: number | null = null;
      let fullReasoningContent = "";
      let collectedCitations: WebSearchCitation[] = [];
      let collectedImages: Array<{ url: string; detail?: string }> = [];
      let throttleTimer: ReturnType<typeof setTimeout> | null = null;
      let streamError: ParsedChatError | null = null;
      let flushAssistantThrottle: () => void = () => {};
      let lastDraftSaveAt = 0;

      try {
        let operating: OperatingTurn | null = null;
        const userQueryText = typeof userMessage.content === "string" ? userMessage.content : String(messageContent);
        if (user?.id && userQueryText.trim()) {
          try {
            operating = await operatingLoopService.prepareTurn({
              userId: user.id,
              question: userQueryText,
              selectedDocumentIds,
              autoSelectBest,
              pickedModel: selectedModel,
              threadId: activeThreadId,
            });
            if (operating) operating = await operatingLoopService.gateContinue(operating);
          } catch (loopErr) {
            console.warn("[useChatSend] operating loop fell through:", loopErr);
            operating = null;
          }
        }
        if (operating && operating.path !== "continue") {
          const stopped: ChatSendMessage = {
            id: assistantId,
            role: "assistant",
            content: operating.message,
            timestamp: new Date(),
            status: "complete",
            receiptLine: operating.receiptLine,
          };
          pendingMessages = [...pendingMessages, stopped];
          updateMessages(pendingMessages);
          const stoppedThreadId = await syncThreadToDatabase(pendingMessages, activeThreadId, "final");
          if (stoppedThreadId) {
            activeThreadId = stoppedThreadId;
            if (!currentThreadId) setCurrentThreadId(stoppedThreadId);
          }
          if (user?.id) {
            void recordChatStopSavings(user.id, operating.path, activeThreadId);
          }
          return;
        }

        let effectiveModel = selectedModel;
        let effectiveThinkingMode = thinkingMode;
        if (operating?.path === "continue" && operating.overrideModel) {
          effectiveModel = operating.model;
          const cur = curatedModels.find((m) => m.id === effectiveModel);
          if (cur?.supportsThinking) effectiveThinkingMode = true;
        } else if (autoSelectBest) {
          const userQuery = typeof userMessage.content === "string" ? userMessage.content : String(messageContent);
          effectiveModel = classifyAndSelectModel(userQuery);
          const cur = curatedModels.find((m) => m.id === effectiveModel);
          if (cur?.supportsThinking) effectiveThinkingMode = true;
        }

        effectiveModel = resolveOpenRouterModelId(effectiveModel);

        if (!effectiveModel?.trim()) {
          effectiveModel = availableModels.find((m) => m.id.includes("gpt-4o") || m.id.includes("gpt-4") || m.id.includes("claude-3.5"))?.id || "openai/gpt-4o-mini";
          effectiveModel = resolveOpenRouterModelId(effectiveModel);
          setSelectedModel(effectiveModel);
          const info = availableModels.find((m) => m.id === effectiveModel);
          if (info) setModelInfo(info);
        }
        if (effectiveModel && availableModels.length > 0) {
          const resolved = resolveOpenRouterModelId(effectiveModel);
          if (!availableModels.some((m) => m.id === resolved)) {
            effectiveModel = availableModels.find((m) => m.id.includes("gpt-4o"))?.id ?? availableModels[0]?.id ?? "openai/gpt-4o-mini";
            effectiveModel = resolveOpenRouterModelId(effectiveModel);
            setSelectedModel(effectiveModel);
            const info = availableModels.find((m) => m.id === effectiveModel);
            if (info) setModelInfo(info);
          } else if (resolved !== effectiveModel) {
            effectiveModel = resolved;
            setSelectedModel(effectiveModel);
          }
        }

        const effectiveModelInfo =
          availableModels.find((m) => m.id === effectiveModel) ??
          (modelInfo && modelInfo.id === effectiveModel ? modelInfo : undefined);

        if (effectiveModelInfo && effectiveModelInfo !== modelInfo) {
          setModelInfo(effectiveModelInfo);
        }

        let messagesToSend = currentMessages.map((m) => ({ role: m.role, content: m.content }));
        const systemMessageParts: string[] = [];

        let contextCitationRecord: Record<number, SourceContext> | undefined;
        try {
          const lastUserMessageText = typeof userMessage.content === "string" ? userMessage.content : String(messageContent);
          await yieldToMain();
          const { systemPrompt: ctxPrompt, contextPayload, phaseMarks } = await buildContextAwareSystemPromptCore({
            threadId: activeThreadId,
            lastUserMessage: lastUserMessageText,
            selectedSpaceIds,
            selectedTopicId,
            selectedDocumentIds: operating?.path === "continue" && operating.shelfIds
              ? operating.shelfIds.filter((id) => !id.includes(":"))
              : selectedDocumentIds,
            shelfIds: operating?.path === "continue" ? operating.shelfIds : undefined,
            usePersonalization,
            hasPersonalizationData,
            sourceOfTruthEnabled: useSourceOfTruth,
            agentId: selectedAgentId,
            selectedSuperPrompt,
            selectedWorkflow,
            useRAG,
            useFullScope,
          });
          chatTiming?.mark(phaseMarks.usedRagPath ? "context_fetch_done_rag" : "context_fetch_done_explicit");
          chatTiming?.mark("prompt_build_done");
          if (operating?.path === "continue" && operating.briefPrompt) systemMessageParts.push(operating.briefPrompt);
          if (ctxPrompt && !(operating?.path === "continue" && operating.workOrderOnly)) systemMessageParts.push(ctxPrompt);
          if (contextPayload?.citationMap?.size) contextCitationRecord = mapToRecord(contextPayload.citationMap);
          const uncapped = contextPayload?.metadata?.uncappedTokensEstimate;
          const packed = contextPayload?.metadata?.totalTokensEstimate;
          if (user?.id && typeof uncapped === "number" && typeof packed === "number" && uncapped > packed) {
            void tokenSavingsService.recordEvent({
              userId: user.id,
              surface: "chat",
              lever: "rag_pack",
              baselineTokens: uncapped,
              actualTokens: packed,
              threadId: activeThreadId,
            });
          }
        } catch (e) {
          console.warn("Failed to build context-aware prompt:", e);
        }
        chatTiming?.mark("context_done");

        const baseModel = effectiveModel.replace(/:online$/i, "");
        const webSearchEnabled = operating?.path === "continue"
          ? operating.outsideInfo && webSearchMode === "open"
          : webSearchMode === "open";

        if (selectedSuperPrompt && !selectedSuperPrompt.isSystem) {
          superPromptsService.incrementUsageCount(selectedSuperPrompt.id).catch(() => {});
        }
        const extSuper = getExternalSuperPromptSystemBlock(selectedSuperPrompt);
        if (extSuper) systemMessageParts.push(extSuper);

        const supportsThinking = curatedModels.find((m) => m.id === effectiveModel)?.supportsThinking ?? false;
        const thinkingExt = buildThinkingChatSystemExtension({
          effectiveThinkingMode: effectiveThinkingMode,
          supportsThinking,
        });
        if (thinkingExt) systemMessageParts.push(thinkingExt);

        const workflowForkExt = await buildWorkflowForkChatSystemExtension(activeThreadId);
        if (workflowForkExt) systemMessageParts.push(workflowForkExt);

        if (selectedGitHubRepo || (selectedGitHubRepos && selectedGitHubRepos.length > 0)) {
          const lastUserMessageText = typeof userMessage.content === "string" ? userMessage.content : String(messageContent);
          const gh = await buildGitHubChatSystemExtension({
            selectedGitHubRepo: selectedGitHubRepo ?? undefined,
            selectedGitHubRepos: selectedGitHubRepos?.length ? selectedGitHubRepos : undefined,
            cachedRepoContext: cachedRepoContextRef.current || cachedRepoContext,
            cachedRepoContexts: cachedRepoContexts ?? cachedRepoContextRef.current,
            lastUserMessageText,
          });
          setRepoEditTurnContext(assistantId, {
            fullFilePaths: gh.fullFilePaths,
            repoTreePaths: gh.repoTreePaths,
            allowRepoEdit: gh.allowRepoEdit,
          });
          systemMessageParts.push(gh.prompt);

          // DS Coder: stack-aware coding standards, error-avoidance rules,
          // golden-stack library policy, and live docs for the attached repos.
          const activeRepos = selectedGitHubRepos?.length
            ? selectedGitHubRepos
            : selectedGitHubRepo
              ? [selectedGitHubRepo]
              : [];
          const dsCoder = await buildDsCoderSystemExtension({
            repos: activeRepos,
            cachedRepoContexts:
              (cachedRepoContexts ?? cachedRepoContextRef.current ?? {}) as Record<string, unknown>,
            lastUserMessageText,
            userId: user?.id ?? null,
            workspaceId: workspaceId ?? null,
          });
          if (dsCoder.prompt) systemMessageParts.push(dsCoder.prompt);
        }
        chatTiming?.mark("repo_done");

        if (operating?.path === "continue" && operating.workOrderOnly && operating.briefPrompt) {
          messagesToSend = [{ role: "system" as const, content: operating.briefPrompt }, ...messagesToSend];
        } else if (systemMessageParts.length) {
          messagesToSend = [{ role: "system" as const, content: systemMessageParts.join("\n\n---\n\n") }, ...messagesToSend];
        }

        const { messages: limitedMessages, stats: historyStats } = limitChatHistoryForApi(messagesToSend);
        let historyBaselineChars = 0;
        try {
          historyBaselineChars = JSON.stringify(messagesToSend).length;
        } catch {
          historyBaselineChars = historyStats.payloadChars;
        }
        messagesToSend = limitedMessages;
        if (user?.id && historyBaselineChars > historyStats.payloadChars) {
          void tokenSavingsService.recordPackFromChars({
            userId: user.id,
            surface: "chat",
            lever: "history_trim",
            baselineChars: historyBaselineChars,
            actualChars: historyStats.payloadChars,
            threadId: activeThreadId,
          });
        }
        logOutboundChatPayload("useChatSend", messagesToSend, {
          threadId: activeThreadId,
          historyTruncation: historyStats,
        });

        chatTiming?.setAssistantAndModel(assistantId, baseModel);

        const msThrottle = getChatStreamUiThrottleMs();
        const DRAFT_SAVE_INTERVAL_MS = 1500;
        const maybeSaveStreamingDraft = () => {
          // Local-only write (no network) so a dead tab / crash mid-stream never loses content
          // that was already visible on screen — independent of whether the eventual DB save
          // succeeds. Coarser than the UI throttle since this touches localStorage.
          if (!pendingPersistence.hasActiveDraft()) return;
          const now = Date.now();
          if (now - lastDraftSaveAt < DRAFT_SAVE_INTERVAL_MS) return;
          lastDraftSaveAt = now;
          pendingPersistence.updateLocalMessages(serializeChatMessages(pendingMessages));
        };
        const applyAssistantContentToMessages = () => {
          updateMessages((p) =>
            p.map((m) =>
              m.id === assistantId
                ? { ...m, content: fullContent, citations: collectedCitations.length ? [...collectedCitations] : m.citations }
                : m
            )
          );
          pendingMessages = pendingMessages.map((m) =>
            m.id === assistantId
              ? { ...m, content: fullContent, citations: collectedCitations.length ? [...collectedCitations] : undefined }
              : m
          );
          maybeSaveStreamingDraft();
        };
        const scheduleAssistantContentFlush = () => {
          if (msThrottle <= 0) {
            applyAssistantContentToMessages();
            return;
          }
          if (throttleTimer != null) return;
          throttleTimer = setTimeout(() => {
            throttleTimer = null;
            applyAssistantContentToMessages();
          }, msThrottle);
        };
        const flushAssistantThrottleImpl = () => {
          if (throttleTimer != null) {
            clearTimeout(throttleTimer);
            throttleTimer = null;
          }
          applyAssistantContentToMessages();
        };
        flushAssistantThrottle = flushAssistantThrottleImpl;

        for await (const chunk of aiService.streamChat({
          model: baseModel,
          messages: messagesToSend,
          modelInfo: effectiveModelInfo as AIServiceModel | undefined,
          webSearchEnabled,
          stream: true,
          timing: chatTiming,
          app: "chat",
        })) {
          try {
            if (chunk.type === "notice" && (chunk as any).content) {
              toast({ title: "Web search", description: String((chunk as any).content) });
              continue;
            }
            if (chunk.type === "error") {
              streamError = parseChatError((chunk as { error?: unknown }).error);
              flushAssistantThrottle();
              break;
            }

            if (chunk.type === "delta" && (chunk as any).content) {
              const content = (chunk as any).content;
              if (!assistantMessageCreated) {
                assistantMessageCreated = true;
                const am: ChatSendMessage = {
                  id: assistantId,
                  role: "assistant",
                  content: "",
                  timestamp: new Date(),
                  model: effectiveModel,
                  autoSelected: autoSelectBest,
                  contextCitations: contextCitationRecord,
                };
                updateMessages((p) => [...p, am]);
                pendingMessages = [...pendingMessages, am];
              }
              fullContent += content;
              scheduleAssistantContentFlush();
            }

            if (chunk.type === "citations" && (chunk as any).citations) {
              flushAssistantThrottle();
              collectedCitations.push(...(chunk as any).citations);
              updateMessages((p) => p.map((m) => (m.id === assistantId ? { ...m, content: fullContent, citations: [...collectedCitations] } : m)));
              pendingMessages = pendingMessages.map((m) => (m.id === assistantId ? { ...m, content: fullContent, citations: [...collectedCitations] } : m));
            }

            if (chunk.type === "images" && (chunk as any).images) {
              flushAssistantThrottle();
              if (!assistantMessageCreated) {
                assistantMessageCreated = true;
                const am: ChatSendMessage = { id: assistantId, role: "assistant", content: "", timestamp: new Date(), model: effectiveModel, autoSelected: autoSelectBest };
                updateMessages((p) => [...p, am]);
                pendingMessages = [...pendingMessages, am];
              }
              collectedImages.push(...(chunk as any).images);
              updateMessages((p) => p.map((m) => (m.id === assistantId ? { ...m, content: fullContent, citations: collectedCitations.length ? [...collectedCitations] : m.citations, images: [...collectedImages] } : m)));
              pendingMessages = pendingMessages.map((m) => (m.id === assistantId ? { ...m, content: fullContent, citations: collectedCitations.length ? [...collectedCitations] : undefined, images: [...collectedImages] } : m));
            }

            if (chunk.type === "reasoning" && (chunk as any).reasoningContent) {
              const reasoning = (chunk as any).reasoningContent;
              fullReasoningContent += reasoning;
              setStreamingThinkingContent((prev) => prev + reasoning);
              if (!assistantMessageCreated) {
                assistantMessageCreated = true;
                const am: ChatSendMessage = {
                  id: assistantId,
                  role: "assistant",
                  content: "",
                  timestamp: new Date(),
                  model: effectiveModel,
                  autoSelected: autoSelectBest,
                  contextCitations: contextCitationRecord,
                };
                updateMessages((p) => [...p, am]);
                pendingMessages = [...pendingMessages, am];
              }
            }

            if (chunk.type === "done") {
              flushAssistantThrottle();
              chatTiming?.mark("stream_end");
              const c = chunk as any;
              if (c.usage) {
                const promptTokens = c.usage.prompt_tokens || 0;
                const completionTokens = c.usage.completion_tokens || 0;
                if (promptTokens || completionTokens) streamTotalTokens = promptTokens + completionTokens;
              }
              if (c.usage && effectiveModelInfo?.pricing) {
                const promptTokens = c.usage.prompt_tokens || 0;
                const completionTokens = c.usage.completion_tokens || 0;
                const promptPrice = parseFloat((effectiveModelInfo.pricing as { prompt?: string }).prompt || "0");
                const completionPrice = parseFloat((effectiveModelInfo.pricing as { completion?: string }).completion || "0");
                try {
                  const { creditsDeducted, hadEnoughBalance } = await creditsService.deductCreditsForAIUsage({
                    promptTokens,
                    completionTokens,
                    promptPrice,
                    completionPrice,
                    model: effectiveModel,
                    profitMultiplier: 3,
                  });
                  if (creditsDeducted > 0) {
                    toast({ title: "Credits Deducted", description: `${creditsDeducted.toLocaleString()} credits used.` });
                    if (!hadEnoughBalance) toast({ title: "Insufficient Credits", description: `Need ${creditsDeducted.toLocaleString()} credits. Balance now 0.`, variant: "destructive" });
                  }
                } catch (err) {
                  console.error("Credit deduction error:", err);
                  toast({ title: "Credit Processing Error", description: "Error processing credits.", variant: "destructive" });
                }
              }
              queryClient.invalidateQueries({ queryKey: ["/api/credits"] });
              queryClient.invalidateQueries({ queryKey: ["credits-overview"] });
              queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
              if (!hasMilestone("first_chat")) completeMilestone("first_chat");
              contentPrefetchService.onThreadComplete();
              break;
            }
          } catch (e) {
            if (e instanceof TypeError) {
              console.error("Error processing chunk (type):", e);
              continue;
            }
            if (e instanceof Error) throw e;
            console.error("Error processing chunk:", e);
          }
        }

        if (!fullContent.trim() && fullReasoningContent.trim()) {
          fullContent = fullReasoningContent.trim();
          flushAssistantThrottle();
          updateMessages((p) =>
            p.map((m) => (m.id === assistantId ? { ...m, content: fullContent } : m))
          );
          pendingMessages = pendingMessages.map((m) =>
            m.id === assistantId ? { ...m, content: fullContent } : m
          );
        }

        const hasPartialContent = fullContent.trim().length > 0 || collectedImages.length > 0;

        if (streamError || !assistantMessageCreated || !hasPartialContent) {
          const parsed =
            streamError ??
            parseChatError(
              !assistantMessageCreated || !hasPartialContent
                ? "No response received from AI model. Please try again."
                : "Streaming failed. Please try again."
            );
          chatTiming?.mark("stream_error");

          if (hasPartialContent) {
            pendingMessages = pendingMessages.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: fullContent,
                    citations: collectedCitations.length ? [...collectedCitations] : m.citations,
                    images: collectedImages.length ? [...collectedImages] : m.images,
                    status: "partial" as const,
                    errorMessage: parsed.message,
                    errorKind: parsed.kind,
                  }
                : m
            );
            updateMessages(pendingMessages);
          } else if (assistantMessageCreated) {
            pendingMessages = pendingMessages.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: "",
                    status: "error" as const,
                    errorMessage: parsed.message,
                    errorKind: parsed.kind,
                  }
                : m
            );
            updateMessages(pendingMessages);
          } else {
            const errorAssistant: ChatSendMessage = {
              id: assistantId,
              role: "assistant",
              content: "",
              timestamp: new Date(),
              model: effectiveModel,
              autoSelected: autoSelectBest,
              status: "error",
              errorMessage: parsed.message,
              errorKind: parsed.kind,
            };
            pendingMessages = [...pendingMessages, errorAssistant];
            updateMessages(pendingMessages);
          }

          const newThreadId = await syncThreadToDatabase(pendingMessages, activeThreadId, "final");
          if (newThreadId) activeThreadId = newThreadId;

          toast({
            title: parsed.title,
            description: parsed.message,
            variant: "destructive",
            action:
              hasPartialContent
                ? React.createElement(
                    ToastAction,
                    {
                      altText: "Continue",
                      onClick: () => setInput("Please continue from where you left off."),
                    },
                    "Continue"
                  )
                : undefined,
          });
          return;
        }

        let receipt: string | undefined;
        if (operating?.path === "continue" && user?.id) {
          const settled = await operatingLoopService.settleAnswer({
            userId: user.id,
            turn: operating,
            answer: fullContent,
            model: effectiveModel,
            totalTokens: streamTotalTokens,
            threadId: activeThreadId,
          });
          fullContent = settled.answer;
          receipt = settled.receiptLine;
        }
        pendingMessages = pendingMessages.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                content: fullContent,
                citations: collectedCitations.length ? [...collectedCitations] : m.citations,
                images: collectedImages.length ? [...collectedImages] : m.images,
                status: "complete" as const,
                errorMessage: undefined,
                errorKind: undefined,
                receiptLine: receipt,
              }
            : m
        );
        updateMessages(pendingMessages);

        const newThreadId = await syncThreadToDatabase(pendingMessages, activeThreadId, "final");
        if (newThreadId) activeThreadId = newThreadId;
      } catch (error) {
        console.error("Chat error:", error);
        const parsed = parseChatError(error);
        console.error("Chat error parsed message:", parsed.message);
        chatTiming?.mark("stream_error");
        if (throttleTimer != null) {
          clearTimeout(throttleTimer);
          throttleTimer = null;
        }
        flushAssistantThrottle();

        const keptPartial =
          assistantMessageCreated && (fullContent.trim().length > 0 || collectedImages.length > 0);

        if (keptPartial) {
          pendingMessages = pendingMessages.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: fullContent,
                  citations: collectedCitations.length ? [...collectedCitations] : m.citations,
                  images: collectedImages.length ? [...collectedImages] : m.images,
                  status: "partial" as const,
                  errorMessage: parsed.message,
                  errorKind: parsed.kind,
                }
              : m
          );
          updateMessages(pendingMessages);
          toast({
            title: parsed.title,
            description: parsed.message,
            variant: "destructive",
            action: React.createElement(
              ToastAction,
              {
                altText: "Continue",
                onClick: () => setInput("Please continue from where you left off."),
              },
              "Continue"
            ),
          });
        } else if (assistantMessageCreated) {
          pendingMessages = pendingMessages.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: "",
                  status: "error" as const,
                  errorMessage: parsed.message,
                  errorKind: parsed.kind,
                }
              : m
          );
          updateMessages(pendingMessages);
          toast({ title: parsed.title, description: parsed.message, variant: "destructive" });
        } else {
          const errorAssistant: ChatSendMessage = {
            id: assistantId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            status: "error",
            errorMessage: parsed.message,
            errorKind: parsed.kind,
          };
          pendingMessages = [...pendingMessages, errorAssistant];
          updateMessages(pendingMessages);
          toast({ title: parsed.title, description: parsed.message, variant: "destructive" });
        }

        try {
          const newThreadId = await syncThreadToDatabase(pendingMessages, activeThreadId, "final");
          if (newThreadId) activeThreadId = newThreadId;
        } catch (persistErr) {
          console.error("[useChatSend] Error persist failed:", persistErr);
          toast({
            title: "Not saved to database yet",
            description:
              "Your messages are stored in browser local storage. Tap Retry save when your connection is back.",
            variant: "destructive",
          });
        }
      } finally {
        chatTiming?.logSummary({ stream_timeout_ms: getChatStreamTimeoutMs() });
        setFileAttached([]);
        setIsStreaming(false);
        setStreamingThinkingContent("");
      }
    },
    [
      input,
      isStreaming,
      isResearching,
      isRecording,
      onStopRecordingAndRequestSubmit,
      deepResearchMode,
      onDeepResearch,
      currentThreadId,
      setCurrentThreadId,
      workspaceId,
      spaceContextId,
      selectedSpaceIds,
      selectedTopicId,
      topicIdFromUrlRef,
      spaceTopics,
      allTopics,
      isTopicsFetching,
      allSelectedSources,
      fileAttached,
      selectedDocumentIds,
      messages,
      updateMessages,
      setInput,
      setFileAttached,
      setServerPersonalization,
      createThreadMutation,
      updateThreadMutation,
      generateMetadataMutation,
      shouldAutoTitle,
      pendingPersistence.beginDraft,
      pendingPersistence.updateLocalMessages,
      pendingPersistence.runSync,
      pendingPersistence.resetDraftSession,
      pendingPersistence.hasActiveDraft,
      selectedModel,
      setSelectedModel,
      modelInfo,
      setModelInfo,
      availableModels,
      isLoadingModels,
      isModelsFetchError,
      modelsFetchError,
      refetchModels,
      curatedModels,
      availableCuratedModels,
      autoSelectBest,
      thinkingMode,
      webSearchMode,
      selectedGitHubRepo,
      cachedRepoContextRef,
      cachedRepoContext,
      isLoadingRepoContext,
      selectedSuperPrompt,
      usePersonalization,
      hasPersonalizationData,
      selectedAgentId,
      selectedWorkflow,
      user,
      completeMilestone,
      hasMilestone,
      contextMode,
      chatMode,
      useRAG,
      useFullScope,
      toast,
      setLocation,
    ]
  );

  const handleContinue = useCallback(
    (_assistantMessageId: string) => {
      if (isStreaming || isResearching) return;
      void handleSend("Please continue from where you left off.");
    },
    [handleSend, isStreaming, isResearching]
  );

  const handleRetry = useCallback(
    (userMessageId: string) => {
      if (isStreaming || isResearching) return;
      void handleSend(undefined, { retryUserMessageId: userMessageId });
    },
    [handleSend, isStreaming, isResearching]
  );

  return {
    handleSend,
    handleContinue,
    handleRetry,
    isStreaming,
    setIsStreaming,
    streamingThinkingContent,
    pendingThreadStatus: pendingPersistence.status,
    pendingThreadLastError: pendingPersistence.lastError,
    retryPendingThreadSync: pendingPersistence.runSync,
    loadPendingDraft: pendingPersistence.loadDraft,
    listUnverifiedPendingDrafts: pendingPersistence.listUnverifiedDrafts,
    listRecoverablePendingDrafts: pendingPersistence.listRecoverableDrafts,
  };
}
